import { TextFieldWidget } from '@tableau/widgets';
import * as queryString from 'query-string';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { style } from 'typestyle';
import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import {
  getSessionManagementServiceInstance,
  SessionManagementService
} from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ContentFiltersKeys } from '../../ts/models/ContentFilters';
import { searchTelemetry } from './SearchTelemetry';

export interface SearchFieldOwnProps {
  readonly searchTerm: string;
}

export type SearchFieldProps = SearchFieldOwnProps & RouteComponentProps;

export interface SearchFieldComponentState {
  readonly searchTerm: string;
  readonly searchTermUpdatePending: boolean;
}

const SearchFieldContainerStyleName: string = style(
  {
    width: '100%'
  }
);

export class SearchFieldComponent extends React.Component<SearchFieldProps, SearchFieldComponentState> {
  private server: SessionManagementService;

  public static readonly InputDebounceMilliseconds = 250;

  public constructor(props: SearchFieldProps) {
    super(props);
    this.state = { searchTerm: props.searchTerm, searchTermUpdatePending: false };

    this.server = getSessionManagementServiceInstance();
    searchTelemetry.ensureInitialization(this.server.isOnlineEnvironment());
  }

  static getDerivedStateFromProps(nextProps: SearchFieldProps, prevState: SearchFieldComponentState): Partial<SearchFieldComponentState> {
    // If we don't have any debounce pending (user hasn't typed into field lately) then take the props search term.
    if (FeatureFlags.searchV2){
      return {};
    }
    if (!prevState.searchTermUpdatePending) {
      return { searchTerm: nextProps.searchTerm };
    }
    return {};
  }

  public shouldComponentUpdate(_nextProps: SearchFieldProps, nextState: SearchFieldComponentState): boolean {
    if (FeatureFlags.searchV2) {
      return true;
    }

    return this.state.searchTerm !== nextState.searchTerm;
  }

  public componentDidMount() {
    if (FeatureFlags.searchV2){
      const query = queryString.parse(this.props.location.search);
      this.setState({searchTerm: query.search});
    }
    if (!searchTelemetry.isSearchFlowStarted()) {
      searchTelemetry.startSearchFlow('search-space');
    }
  }

  public componentWillUnmount() {
    searchTelemetry.endSearchFlowWithNoResultEvent('search-space', this.state.searchTerm);
  }

  public componentDidUpdate(prevProps: SearchFieldProps) {
    if (FeatureFlags.searchV2){
      const query = queryString.parse(this.props.location.search);
      const prevQuery = queryString.parse(prevProps.location.search);

      if (prevQuery.search !== query.search){
        this.setState({searchTerm: query.search});
      }
    }
  }

  public render() {
    // The missing "aria-label" is not introduced by replacing SearchFieldWidget with TextFieldWidget.
    // In order to hide the label, we still have to set label to null.
    // So add the following TODO item to address this issue later.
    // TODO: Story 1160152: [Tech Debt] Search textbox in SERP need to have a "aria-label" for accessibility purpose.
    return <TextFieldWidget
      kind="search"
      label={null}
      testId='search-field'
      className={SearchFieldContainerStyleName}
      style={{ width: '100%' }}
      onClear={this.handleCancelSearch}
      onChange={this.handleSearchChange}
      placeholder={Messages.searchboxPlaceholder()}
      value={this.state.searchTerm}
      onFocus={this.handleFocus}
    />;
  }

  private handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: event.target.value, searchTermUpdatePending: true });
    this.debounceupdateSearchTerm(event.target.value);
  }

  private handleCancelSearch = () => {
    this.setState({ searchTerm: '' });
    this.updateSearchTerm('');
  }

  private handleFocus = () => {
    if (!this.state.searchTerm || !searchTelemetry.isSearchFlowStarted()) {
      searchTelemetry.startSearchFlow('search-space');
    }
  }

  private debounceupdateSearchTerm = _.debounce(this.updateSearchTerm, SearchFieldComponent.InputDebounceMilliseconds);
  private updateSearchTerm(text: string) {
    const query = new URLSearchParams(this.props.location.search);
    if (text) {
      query.set(ContentFiltersKeys.search, text);
    } else {
      query.delete(ContentFiltersKeys.search);
    }

  // The path here is for the fragment after the '#', and spaces here
  // can't be encoded as '+'. We URL decoding these explicitly here
  // knowing that the spaces will get url encoded properly when
  // the history is updated.
    const queryString = query.toString().replace(/\+/g, '%20');

    this.props.history.push({
      ...this.props.location,
      search: queryString
    });

    setTimeout(() => {
      // Only reset update pending if the text we've just updated url with
      // matches the text in the search input.
      // This prevents incorrect searchTerm updates between the time of the push to history and typing
      if (this.state.searchTerm === text) {
        this.setState({ searchTermUpdatePending: false });
        searchTelemetry.sendSearchTermUpdatedEvent('search-space', text);
      }
    });
  }
}

export const SearchField: React.ComponentType<SearchFieldOwnProps> = withRouter(SearchFieldComponent);
